import React, {useEffect, useRef, useState} from "react";
import {Link} from "react-router-dom";
import {FiBookmark, FiMoreHorizontal, FiShare2, FiX} from "react-icons/fi";
import {useTranslation} from "../../i18n/LanguageProvider";
import {itineraryKey, readShortlist, SHORTLIST_KEY, useShortlistStore} from "../../store/useShortlistStore";
import {ShortlistService} from "../../services/ShortlistService";
import ShortlistFlight from "./ShortlistFlight";

const Bookmark = FiBookmark as unknown as React.FC<{size?: number}>;
const Share = FiShare2 as unknown as React.FC<{size?: number}>;
const More = FiMoreHorizontal as unknown as React.FC<{size?: number}>;
const Close = FiX as unknown as React.FC<{size?: number}>;

export default function ShortlistNavigation() {
  const {locale, t} = useTranslation();
  const state = useShortlistStore();
  const dialog = useRef<HTMLDialogElement>(null);
  const [manualCopy, setManualCopy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmStop, setConfirmStop] = useState(false);

  useEffect(() => {
    const sync = (event: StorageEvent) => {
      if (event.key === SHORTLIST_KEY && !useShortlistStore.getState().busy) useShortlistStore.setState(readShortlist());
    };
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);

  useEffect(() => {
    if (!state.open) return;
    const element = dialog.current;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    element?.showModal();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (!useShortlistStore.getState().error) void useShortlistStore.getState().refresh();
    return () => {
      element?.close();
      document.body.style.overflow = overflow;
      previouslyFocused?.focus();
    };
  }, [state.open]);

  useEffect(() => {setManualCopy(false); setCopied(false); setConfirmStop(false);}, [state.open]);

  const share = async () => {
    const url = await state.share();
    if (!url) return;
    try { await navigator.clipboard.writeText(url); setCopied(true); setManualCopy(false); }
    catch { setCopied(false); setManualCopy(true); }
  };
  const activeLink = state.sharedId ? ShortlistService.url(state.sharedId) : "";

  return <>
    <nav aria-label={t("shortlist.title")} className="fixed bottom-5 right-4 z-40 sm:bottom-6 sm:right-6" style={{bottom: "max(1.25rem, env(safe-area-inset-bottom))"}}>
      <button type="button" onClick={() => state.setOpen(true)} aria-haspopup="dialog" aria-expanded={state.open}
        aria-label={t("shortlist.nav", {count: state.flights.length})}
        title={t("shortlist.title")}
        className="flex min-h-[48px] items-center gap-2.5 rounded-full border border-[#DED5F8] bg-white px-4 text-sm font-bold text-[#712FFF] shadow-[0_4px_24px_rgba(85,45,140,0.18)] transition hover:-translate-y-0.5 hover:bg-[#F4F0FF] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#712FFF]">
        <Bookmark size={19}/><span className="hidden sm:inline">{t("shortlist.title")}</span>
        <span className="rounded-full bg-[#EEE5FF] px-2 py-0.5 tabular-nums" aria-hidden="true">{state.flights.length}</span>
      </button>
    </nav>
    {state.open && <dialog ref={dialog} aria-labelledby="shortlist-heading"
      onCancel={() => state.setOpen(false)}
      onClick={event => {if (event.target === dialog.current) state.setOpen(false);}}
      className="fixed inset-y-0 left-auto right-0 m-0 ml-auto h-[100dvh] max-h-none w-full max-w-lg overflow-hidden border-0 bg-[#F8F6FB] p-0 text-[#26213B] shadow-2xl backdrop:bg-[#261541]/40">
      <div className="flex h-full flex-col" onClick={event => event.stopPropagation()}>
        <header className="flex shrink-0 items-center justify-between gap-3 border-b border-[#EDE6F5] bg-white px-5 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <span aria-hidden="true" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[#F0E9FE] text-[#712FFF]"><Bookmark size={19}/></span>
            <h1 id="shortlist-heading" className="truncate text-lg font-bold">{t("shortlist.title")}</h1>
            <span className="rounded-full bg-[#F0E9FE] px-2 py-0.5 text-xs font-bold tabular-nums text-[#8758CF]">{state.flights.length}</span>
          </div>
          <button type="button" autoFocus onClick={() => state.setOpen(false)} aria-label={t("shortlist.close")}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[#9B8CAC] hover:bg-[#F4F0FF] hover:text-[#712FFF]"><Close size={20}/></button>
        </header>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain p-3 sm:p-4">
          {state.error && <p role="alert" className="rounded-xl bg-red-50 p-3 text-sm text-red-700">{t(state.error)}</p>}
          {state.flights.length === 0 ? <div className="py-16 text-center">
            <h2 className="text-xl font-bold">{t("shortlist.empty")}</h2><p className="mt-3 text-sm text-[#6A6480]">{t("shortlist.emptyHint")}</p>
          </div> : state.flights.map(flight => <article key={itineraryKey(flight)} className="rounded-2xl border border-[#E9E2F2] bg-white p-3 shadow-[0_2px_8px_rgba(52,29,79,0.025)]">
            <ShortlistFlight flight={flight} action={<button type="button" disabled={state.busy}
              aria-label={t("shortlist.remove")} title={t("shortlist.remove")} onClick={() => void state.toggle(flight)}
              className="-mr-1 -mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[#B4A7C3] hover:bg-red-50 hover:text-red-500 disabled:opacity-50"><Close size={15}/></button>}/>
          </article>)}
          {state.expiresAt && <p className="text-xs text-[#6A6480]">{t("shortlist.expiry", {date: new Date(state.expiresAt * 1000).toLocaleDateString(locale)})}</p>}
        </div>
        <footer className="shrink-0 space-y-3 border-t border-[#EDE6F5] bg-white px-4 py-4 sm:px-5" style={{paddingBottom: "max(1rem, env(safe-area-inset-bottom))"}}>
          <div className="flex items-center gap-2">
            <button type="button" disabled={!state.flights.length || state.busy} onClick={() => void share()}
              className="flex min-h-[44px] min-w-0 flex-1 items-center justify-center gap-2 rounded-full bg-[#712FFF] px-4 text-sm font-bold text-white transition hover:bg-[#6024DA] disabled:opacity-50"><Share size={15}/>{t(state.busy ? "shortlist.working" : copied ? "share.copied" : "shortlist.share")}</button>
            {activeLink && <>
              <Link to={`/shortlists/${state.sharedId}`} onClick={() => state.setOpen(false)} className="inline-flex min-h-[44px] items-center whitespace-nowrap rounded-full bg-[#F3EEFC] px-4 text-xs font-bold text-[#712FFF] hover:bg-[#EAE0FC]">{t("shortlist.view")}</Link>
              <details className="group relative">
                <summary aria-label={t("shortlist.options")} title={t("shortlist.options")} className="flex h-10 w-10 cursor-pointer list-none items-center justify-center rounded-full text-[#9B8CAC] hover:bg-[#F4F0FF] [&::-webkit-details-marker]:hidden"><More size={19}/></summary>
                <div className="absolute bottom-full right-0 z-10 mb-3 w-64 rounded-2xl border border-[#E9E2F2] bg-white p-3 shadow-lg">
                  {confirmStop ? <div className="space-y-2"><p className="text-xs leading-relaxed text-[#6A6480]">{t("shortlist.stopHint")}</p>
                    <button type="button" disabled={state.busy} onClick={() => void state.stopSharing()} className="min-h-[44px] text-left text-xs font-bold text-red-700">{t("shortlist.confirmStop")}</button>
                    <button type="button" onClick={() => setConfirmStop(false)} className="min-h-[36px] text-xs text-[#6A6480]">{t("shortlist.cancel")}</button>
                  </div> : <button type="button" disabled={state.busy} onClick={() => setConfirmStop(true)} className="min-h-[40px] w-full rounded-lg px-2 text-left text-xs font-semibold text-[#6A6480] hover:bg-[#F8F6FB]">{t("shortlist.stop")}</button>}
                </div>
              </details>
            </>}
          </div>
          {activeLink && manualCopy && <label className="block text-xs text-[#6A6480]">{t("share.copyManually")}
            <input aria-label={t("shortlist.share")} readOnly value={activeLink} onFocus={event => event.target.select()}
              className="mt-1 w-full rounded-lg border border-[#DED5F8] p-2 text-sm"/></label>}
        </footer>
      </div>
    </dialog>}
  </>;
}
